import {
	App,
	AppMenuPoint,
	AppMenuPoints,
	AppNavMenuPointsService,
	BreakpointObserverService,
	ErrorService,
	ErrorType,
	getColor,
	getWidgetTextColor,
	NavVariant,
	Role,
	SummaryOfTheYearService,
	Vehicle,
} from '@agilox/common';
import {
	CookieOverlayComponent,
	ErrorContentComponent,
	ErrorPageComponent,
	HelpContentComponent,
	SummaryOfTheYearComponent,
	ToastContainerComponent,
} from '@agilox/components';
import { NavModule, OnboardingComponent, OverlayComponent } from '@agilox/ui';
import { MenuPoint, MenuPointCallbackAction, Sizes } from '@agilox/ui-common';
import { LowerCasePipe } from '@angular/common';
import {
	AfterViewInit,
	Component,
	computed,
	inject,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@auth';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserStoreService } from '@user-store';
import { Chart } from 'chart.js';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';
import { combineLatest, filter, map, Observable, shareReplay, startWith, switchMap } from 'rxjs';
import { analyticsOnboarding } from './general/constants/analytics-onboarding.const';
import { AppSettings } from './general/interfaces/app-settings.interface';
import { BoardConfig } from './general/interfaces/board-config.interface';
import { MenuPointUrl } from './general/shared/enums';
import { AnalyticsMenuPointService } from './general/shared/services/analytics-menupoint/analytics-menupoint.service';
import { ChartMigrationService } from './general/shared/services/chart-migration/chart-migration.service';
import { OnboardingService } from './general/shared/services/onboarding/onboarding.service';
import { UrlUtilsService } from './general/shared/services/url-utils/url-utils.service';
import { getEnvironmentFromUrl } from './general/utils/environment.util';
import { initAppSettings } from './store/app-settings.actions';
import { loadVehicles } from './store/vehicles/vehicles.actions';

@Component({
	selector: 'analytics-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		NavModule,
		RouterOutlet,
		OverlayComponent,
		ErrorContentComponent,
		ErrorPageComponent,
		HelpContentComponent,
		SummaryOfTheYearComponent,
		OnboardingComponent,
		ToastContainerComponent,
		CookieOverlayComponent,
		LowerCasePipe,
		TranslatePipe,
	],
})
export class AppComponent implements OnInit, AfterViewInit {
	private breakpointObserverService = inject(BreakpointObserverService);
	private userStoreService = inject(UserStoreService);
	private translate = inject(TranslateService);
	private authService = inject(AuthService);
	private router = inject(Router);
	protected errorService = inject(ErrorService);
	private analyticsMenuPointService = inject(AnalyticsMenuPointService);
	private appNavMenuPointsService = inject(AppNavMenuPointsService);
	private store = inject(Store);
	private onboardingService = inject(OnboardingService);
	private chartMigrationService = inject(ChartMigrationService);
	private urlUtilsService = inject(UrlUtilsService);
	private summaryOfTheYearService = inject(SummaryOfTheYearService);

	public emailExtras = computed(() => {
		return {
			settings: JSON.stringify(this.emailBodyData()),
		};
	});

	private emailBodyData = computed(() => {
		const appSettings = this.userStoreService.appSettings();
		if (!appSettings) return {};

		return this.currentUrl().includes(MenuPointUrl.dashboards)
			? { ...this.getCurrentBoard(appSettings) }
			: { selectedMachines: appSettings.selectedMachines, dateSelector: appSettings.dateSelector };
	});

	title = 'AGILOX Analytics';

	private role$ = this.userStoreService.role$.pipe(
		filter((role): role is Role => !!role),
		shareReplay(1)
	);

	private language$ = this.translate.onLangChange.pipe(
		startWith({ lang: this.translate.currentLang || 'en' }),
		shareReplay(1)
	);

	public summaryOpen = this.summaryOfTheYearService.isSummaryOpen;

	private roleAndLanguage$ = combineLatest([this.role$, this.language$]);

	public desktopMenuPoints$ = this.roleAndLanguage$.pipe(
		switchMap(([role, _]) =>
			this.analyticsMenuPointService.getMenuPointsWithSubmenus(NavVariant.DESKTOP, role as Role)
		),
		shareReplay(1)
	);

	public mobileMenuPoints$ = this.roleAndLanguage$.pipe(
		switchMap(([role, _]) =>
			this.analyticsMenuPointService.getMenuPointsWithSubmenus(NavVariant.MOBILE, role as Role)
		),
		shareReplay(1)
	);

	public appNavMenuPoints$ = this.roleAndLanguage$.pipe(
		map(([role, _]) => this.appNavMenuPointsService.getAppNavMenuPoints(role as Role)),
		startWith([]),
		shareReplay(1)
	);

	activeApp: AppMenuPoint | undefined = AppMenuPoints.find((app) => app.name === 'analytics');
	error$ = this.errorService.error$;

	helpOpen: boolean = false;

	displayMode = this.urlUtilsService.isDisplayModeActive;

	private _routerEvents$: Observable<any> = this.router.events.pipe(
		takeUntilDestroyed(),
		shareReplay(1)
	);
	private _navigationEnd$: Observable<NavigationEnd> = this._routerEvents$.pipe(
		filter((event) => event instanceof NavigationEnd)
	);

	public pageName$ = this._navigationEnd$.pipe(
		startWith({ urlAfterRedirects: this.router.url }),
		map((event) => {
			return 'p-' + event.urlAfterRedirects.replace('/', '').replace(/\//g, '-').split('?')[0];
		})
	);

	public currentUrl$ = this._navigationEnd$.pipe(
		startWith({ urlAfterRedirects: this.router.url }),
		map((event) => event.urlAfterRedirects)
	);

	public pageName = toSignal(this.pageName$);
	public currentUrl = toSignal(this.currentUrl$);
	public mobileMenuPoints = toSignal(this.mobileMenuPoints$);
	public desktopMenuPoints = toSignal(this.desktopMenuPoints$);
	public appNavMenuPoints = toSignal(this.appNavMenuPoints$);
	public error = toSignal(this.error$);

	/**
	 * Will get triggered when a navigation chunk fails to load because the user is offline
	 * or the chunk is not available (e.g. due to a new version of the app)
	 * @private
	 */
	private _navigationError$: Observable<NavigationError> = this._routerEvents$.pipe(
		filter((event) => event instanceof NavigationError)
	);

	onBoardingWatched = this.onboardingService.onboardingWatched;
	public readonly analyticsOnboarding = analyticsOnboarding;

	environment: string = getEnvironmentFromUrl(window.location.href);

	constructor() {
		this._navigationError$.subscribe((event) => {
			if (!navigator.onLine) {
				this.errorService.setError({
					application: App.analytics,
					type: ErrorType.NO_INTERNET_ERROR,
				});
			} else if (event.error.toString().includes('chunk')) {
				this.errorService.setError({
					application: App.analytics,
					type: ErrorType.CHUNK_ERROR,
				});
			}
		});

		// When the current user couldn't be loaded we render the error page instead
		// of the shell, so skip the user-dependent setup that would otherwise crash.
		if (this.errorService.serverError()) {
			return;
		}

		if (!this.chartMigrationService.isWidgetMigrationComplete()) {
			this.chartMigrationService.migrateExistingWidgets();
		}
		this.setupChartJs();
	}

	ngOnInit() {
		if (this.errorService.serverError()) {
			return;
		}
		this.store.dispatch(
			loadVehicles({ value: this.userStoreService.appSettings()?.selectedMachines || [] })
		);
	}

	ngAfterViewInit(): void {
		if (this.errorService.serverError()) {
			return;
		}
		this.store.dispatch(initAppSettings());
		// ios fix
		if (this.breakpointObserverService.isMobile() || this.breakpointObserverService.isTablet()) {
			const doc = document.documentElement;
			doc.style.height = `${window.innerHeight}px`;
		}
	}

	setOnboardingToWatched() {
		this.onboardingService.markOnboardingAsWatched().subscribe();
	}

	navCallback(menuPoint: MenuPoint) {
		if (menuPoint.callbackAction === MenuPointCallbackAction.HELP) {
			this.helpOpen = true;
			return;
		}

		if (menuPoint.callbackAction === MenuPointCallbackAction.LOGOUT) {
			this.logout();
			return;
		}
	}

	logout(): void {
		this.authService.logout();
	}

	onClose() {
		this.errorService.clearError();
	}

	onCloseHelp() {
		this.helpOpen = false;
	}

	private setupChartJs() {
		Chart.register(TreemapController, TreemapElement);
		Chart.defaults.color = getWidgetTextColor();
		Chart.defaults.plugins.tooltip.backgroundColor = getColor('--color-secondary-900');
		Chart.defaults.plugins.tooltip.titleColor = getColor('--color-white');
		Chart.defaults.plugins.tooltip.bodyColor = getColor('--color-white');
		Chart.defaults.plugins.tooltip.footerColor = getColor('--color-white');
	}

	private getCurrentBoard(appSettings: AppSettings): BoardConfig {
		const board = appSettings.boards.find(
			(board) => board.id === this.currentUrl().split('/').at(-1)
		);

		if (!board) {
			return {} as BoardConfig;
		}

		if (board.dashboardFilter) {
			board.dashboardFilter.vehicles = board.dashboardFilter.vehicles?.map((vehicle) => {
				return { name: vehicle.name, serial: vehicle.serial };
			}) as Vehicle[];
		}
		if (board.statusboardFilter) {
			board.statusboardFilter.vehicles = board.statusboardFilter.vehicles?.map((vehicle) => {
				return { name: vehicle.name, serial: vehicle.serial };
			}) as Vehicle[];
		}
		return board;
	}

	protected readonly Sizes = Sizes;
}
